/**
 * Effective user count for per-user products (AI Conversation, Omnichannel Chat, Eniq AI).
 *
 * These products are priced per user, and the user count is derived from the
 * Business Phone / FMC configuration rather than entered separately.
 *
 * Mirrors `minUsersByTier` in backend/services/pricing_service.go — keep in sync.
 */

export const MIN_USERS_BY_TIER = {
  basic: 3,
  pro: 3,
  corporate: 5,
  advanced: 5,
};

export const DEFAULT_MIN_USERS = 3;
export const FMC_MIN_USERS = 6;

/** Minimum billable package for the Omnichannel Chat add-on (mirrors backend). */
export const CHAT_MIN_USERS = 3;

export const getMinUsersForTier = (packageTier) =>
  MIN_USERS_BY_TIER[packageTier] ?? DEFAULT_MIN_USERS;

/**
 * @param {Object} params
 * @param {number|string} params.businessPhoneUsers
 * @param {number|string} params.fmcUsers
 * @param {string} params.businessPhoneType 'business-phone' | 'fmc' | 'business-phone-fmc'
 * @param {string} params.packageTier 'basic' | 'pro' | 'advanced' | 'corporate'
 * @returns {number}
 */
export const getEffectiveTierUsers = ({
  businessPhoneUsers,
  fmcUsers,
  businessPhoneType,
  packageTier,
} = {}) => {
  const bpUsers = Number(businessPhoneUsers) || 0;
  const fUsers = Number(fmcUsers) || 0;

  if (businessPhoneType === 'fmc') {
    return Math.max(fUsers, FMC_MIN_USERS);
  }

  if (businessPhoneType === 'business-phone-fmc') {
    return bpUsers + fUsers;
  }

  return Math.max(bpUsers, getMinUsersForTier(packageTier));
};

/**
 * Effective user count for Omnichannel Chat.
 *
 * Chat differs from the other per-user products: it is an add-on on top of
 * Business Phone workspace users only. FMC users have no smartphone app and
 * therefore can never be chat users, so they are excluded from the count.
 *
 * @param {Object} params
 * @param {number|string} params.businessPhoneUsers
 * @param {string} params.businessPhoneType 'business-phone' | 'fmc' | 'business-phone-fmc'
 * @param {string} params.packageTier 'basic' | 'pro' | 'advanced' | 'corporate'
 * @returns {number} 0 in FMC-only mode, where chat is unavailable
 */
export const getEffectiveChatUsers = ({
  businessPhoneUsers,
  businessPhoneType,
  packageTier,
} = {}) => {
  if (businessPhoneType === 'fmc') {
    return 0;
  }

  const bpUsers = Number(businessPhoneUsers) || 0;

  // In combined mode the tier minimum is covered by BP + FMC together, so the
  // only floor left for chat is the add-on's own minimum package.
  const floor = businessPhoneType === 'business-phone-fmc'
    ? CHAT_MIN_USERS
    : getMinUsersForTier(packageTier);

  return Math.max(bpUsers, floor);
};
